import {Controller} from "@hotwired/stimulus"

//
//
export default class extends Controller {
    static targets = ["input", "id", "zone", "card", "name", "meta", "preview", "progress", "bar", "error"]
    static values = {
        url: String,
        maxSize: {type: Number, default: 10 * 1024 * 1024},
        accept: {type: String, default: "application/pdf"}
    }

    connect() {
        this.depth = 0
    }

    dragEnter(event) {
        event.preventDefault()
        this.depth += 1
        this.zoneTarget.dataset.dragging = ""
    }

    dragOver(event) {
        event.preventDefault()
    }

    dragLeave(event) {
        event.preventDefault()
        this.depth = Math.max(0, this.depth - 1)
        if (this.depth === 0) this.clearHighlight()
    }

    drop(event) {
        event.preventDefault()
        this.clearHighlight()

        const file = event.dataTransfer.files[0]
        if (!file) return

        const transfer = new DataTransfer()
        transfer.items.add(file)
        this.inputTarget.files = transfer.files

        this.upload(file)
    }

    change() {
        const file = this.inputTarget.files[0]
        if (file) this.upload(file)
    }

    upload(file) {
        this.hideError()

        if (this.acceptValue && file.type !== this.acceptValue) {
            return this.fail("That is not a PDF. Save the purchase order as a PDF and try again.")
        }
        if (file.size > this.maxSizeValue) {
            return this.fail(`That file is ${this.humanSize(file.size)}. The limit is ${this.humanSize(this.maxSizeValue)}.`)
        }

        const reader = new FileReader()
        reader.addEventListener("load", () => this.transmit(reader.result, file))
        reader.readAsDataURL(file)
    }

    transmit(dataUrl, file) {
        this.showProgress()

        const request = new XMLHttpRequest()
        request.open("POST", this.urlValue, true)
        request.setRequestHeader("Content-Type", "application/json")
        request.withCredentials = true

        request.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) this.barTarget.style.width = `${Math.round((event.loaded / event.total) * 100)}%`
        })

        request.addEventListener("load", () => {
            this.hideProgress()

            let json
            try {
                json = JSON.parse(request.responseText)
            } catch (error) {
                return this.fail("The upload did not go through. Try again.")
            }

            if (json.errors) return this.fail(json.errors)

            // Optional: return orders upload the document but have nowhere to bind
            // it — return_orders has no purchase_order_id column.
            if (this.hasIdTarget) this.idTarget.value = json.id
            this.showCard(file.name, this.humanSize(file.size), json.url)
        })

        request.addEventListener("error", () => {
            this.hideProgress()
            this.fail("The upload did not go through. Try again.")
        })

        request.send(JSON.stringify({file: dataUrl}))
    }

    remove(event) {
        event.preventDefault()

        if (this.hasIdTarget) this.idTarget.value = ""
        this.inputTarget.value = ""
        this.cardTarget.classList.add("ice:hidden")
        this.zoneTarget.classList.remove("ice:hidden")
        this.hideError()
    }

    showCard(name, size, url) {
        this.nameTarget.textContent = name
        this.metaTarget.textContent = size

        if (url) {
            this.previewTarget.href = url
            this.previewTarget.classList.remove("ice:hidden")
        } else {
            this.previewTarget.classList.add("ice:hidden")
        }

        this.cardTarget.classList.remove("ice:hidden")
        this.zoneTarget.classList.add("ice:hidden")
    }

    showProgress() {
        this.barTarget.style.width = "0%"
        this.progressTarget.classList.remove("ice:hidden")
        this.zoneTarget.classList.add("ice:hidden")
    }

    hideProgress() {
        this.progressTarget.classList.add("ice:hidden")
        if (this.cardTarget.classList.contains("ice:hidden")) this.zoneTarget.classList.remove("ice:hidden")
    }

    fail(message) {
        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("ice:hidden")
        this.inputTarget.value = ""
        this.zoneTarget.classList.remove("ice:hidden")
    }

    hideError() {
        this.errorTarget.classList.add("ice:hidden")
    }

    clearHighlight() {
        this.depth = 0
        delete this.zoneTarget.dataset.dragging
    }

    humanSize(bytes) {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
}
